import { createSelector } from 'reselect';
import { isEmpty, get } from 'lodash';
import moment from 'moment';
import {
  SCHEDULING_RECURRENCE,
  // SCHEDULING_IMMEDIATE,
  CRON_INTERVAL_ONCE,
  CRON_CUSTOM_DATES,
  SCHEDULING_SCHEDULED,
} from './constants';
import {
  getRepeatDates,
  hasOlderCustomDatesThanLaunchDate,
  isPastDate,
} from './utils';

const selectSchedulingDomain = (state) => get(state, 'scheduling', {});
const selectSchedulingConfigDomain = (state) =>
  get(state, 'survey.surveyDetails.scheduling', {});
const releaseDatesDomain = createSelector(selectSchedulingDomain, (state) =>
  getRepeatDates(state),
);

const makeSelectErrors = createSelector(selectSchedulingDomain, (substate) => {
  const {
    startDate,
    endDate,
    time,
    timezone,
    interval,
    schedulingType,
    custom = [],
  } = substate;
  const errors = {};

  if (schedulingType === SCHEDULING_RECURRENCE) {
    if (!startDate) {
      errors.missingStartDate = true;
    } else if (
      isPastDate({
        selectedDate: startDate,
        timezone,
        time,
      })
    ) {
      errors.pastDate = true;
    }
  }

  if (schedulingType === SCHEDULING_RECURRENCE) {
    if (
      !endDate &&
      interval !== CRON_INTERVAL_ONCE &&
      interval !== CRON_CUSTOM_DATES
    ) {
      errors.missingEndDate = true;
    }

    if (!timezone) {
      errors.missingTimezone = true;
    }

    if (!time) {
      errors.missingTime = true;
    }

    if (startDate && endDate) {
      if (moment(startDate).isAfter(endDate)) {
        errors.startAfterEnd = true;
      }
    }
  }

  if (interval === CRON_CUSTOM_DATES) {
    let customDateToCheck;
    errors.custom = {};
    for (let i = 0; i < custom.length; i += 1) {
      customDateToCheck = custom[i];
      if (!customDateToCheck.date) {
        if (!errors.custom[customDateToCheck.refId]) {
          errors.custom[customDateToCheck.refId] = {};
        }
        errors.custom[customDateToCheck.refId] = { date: true };
      } else if (
        isPastDate({
          selectedDate: customDateToCheck.date,
          timezone,
          time: customDateToCheck.time,
        })
      ) {
        errors.custom[customDateToCheck.refId] = { pastDate: true };
      } else if (
        hasOlderCustomDatesThanLaunchDate(startDate, custom, interval)
      ) {
        errors.hasOlderCustomDatesThanLaunchDate = true;
      }
      if (!customDateToCheck.time) {
        if (!errors.custom[customDateToCheck.refId]) {
          errors.custom[customDateToCheck.refId] = {};
        }
        errors.custom[customDateToCheck.refId].time = true;
      }
    }
    if (isEmpty(errors.custom)) {
      delete errors.custom;
    }
  }

  return errors;
});

const makeSelectCron = createSelector(
  selectSchedulingDomain,
  (substate) => substate.cron,
);

const makeSelectInterval = createSelector(
  selectSchedulingDomain,
  (substate) => substate.interval,
);

const makeSelectStartDate = createSelector(
  selectSchedulingDomain,
  (substate) => substate.startDate,
);

const makeSelectStartDateAsMs = createSelector(
  selectSchedulingDomain,
  (substate) => {
    const { startDate } = substate;
    return startDate.valueOf();
  },
);

const makeSelectEndDateAsMs = createSelector(
  selectSchedulingDomain,
  (substate) => {
    const { endDate } = substate;
    return endDate.valueOf();
  },
);

const makeSelectEndDate = createSelector(
  selectSchedulingDomain,
  (substate) => substate.endDate,
);

const makeSelectTime = createSelector(
  selectSchedulingDomain,
  (substate) => substate.time,
);

const makeSelectTimezone = createSelector(
  selectSchedulingDomain,
  (substate) => substate.timezone,
);

const makeSelectSchedulingType = createSelector(
  selectSchedulingDomain,
  (substate) => substate.schedulingType,
);

const makeSelectRepeatDates = createSelector(
  releaseDatesDomain,
  (substate) => substate,
);

const makeSelectRepeatCount = createSelector(
  releaseDatesDomain,
  selectSchedulingDomain,
  (releaseDates, scheduling) => {
    const { interval, custom = [] } = scheduling;
    if (interval === CRON_CUSTOM_DATES) {
      return custom.length + 1;
    }
    if (releaseDates) {
      return releaseDates.repeats;
    }
    return 1;
  },
);

const makeSelectScheduling = createSelector(
  selectSchedulingDomain,
  (substate) => substate,
);

const makeSelectSchedulingConfig = createSelector(
  selectSchedulingConfigDomain,
  (substate) => substate,
);

const makeSelectLaunchNowAllowed = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isLaunchNowAllowed,
);

const makeSelectSchedulingAllowed = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isSchedulingAllowed,
);

const makeSelectSurveyStatusName = createSelector(
  selectSchedulingDomain,
  (substate) => substate.statusName,
);

const makeSelectExistingScheduling = createSelector(
  selectSchedulingDomain,
  (substate) => substate.existingScheduling,
);

const makeSelectIsPristineStartDate = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isPristineStartDate,
);

const makeSelectIsPristineEndDate = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isPristineEndDate,
);

const makeSelectIsPristineTimezone = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isPristineTimezone,
);

const makeSelectIsPristineTime = createSelector(
  selectSchedulingDomain,
  (substate) => substate.isPristineTime,
);

const makeSelectCustomDates = createSelector(
  selectSchedulingDomain,
  (substate) => substate.custom || [],
);

const makeSelectIsScheduled = createSelector(
  makeSelectSchedulingType,
  (schedulingType) =>
    [SCHEDULING_SCHEDULED, SCHEDULING_RECURRENCE].includes(schedulingType),
);

const makeSelectIsRecurring = createSelector(
  makeSelectSchedulingType,
  (schedulingType) => schedulingType === SCHEDULING_RECURRENCE,
);

export default makeSelectScheduling;

export {
  makeSelectCron,
  makeSelectStartDate,
  makeSelectEndDate,
  makeSelectTime,
  makeSelectTimezone,
  makeSelectInterval,
  makeSelectStartDateAsMs,
  makeSelectEndDateAsMs,
  makeSelectSchedulingType,
  makeSelectRepeatDates,
  makeSelectRepeatCount,
  makeSelectLaunchNowAllowed,
  makeSelectSchedulingAllowed,
  makeSelectErrors,
  makeSelectSurveyStatusName,
  makeSelectExistingScheduling,
  makeSelectIsPristineStartDate,
  makeSelectIsPristineEndDate,
  makeSelectIsPristineTimezone,
  makeSelectIsPristineTime,
  makeSelectCustomDates,
  makeSelectIsScheduled,
  makeSelectIsRecurring,
  makeSelectSchedulingConfig,
};
